import Box from "@mui/material/Box";
import { useWindowSize } from "@uidotdev/usehooks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { PanelSize } from "react-resizable-panels";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";
import { matchPath, Navigate, Outlet, useLocation } from "react-router-dom";

import { FeedPanelContext } from "@/components/DriverView/ui/FeedPanelContext";
import MiddleColumn from "@/components/MiddleTableNavigation/MiddleColumn.tsx";
import { BootBlockingScope } from "@/components/NewDesign/BootBlockingOverlay";
import LeftSidebar from "@/components/NewDesign/LeftSidebar.tsx";
import {
  dispatchPathForDriverView,
  isChannelFeedPath,
  isDispatchPath,
  isDriverFeedPath,
} from "@/components/NewDesign/newDesignUtils";
import InviteToWorkspaceModal from "@/components/Workspace/InviteToWorkspaceModal.tsx";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import { useBootIndicatorLook } from "@/hooks/useBootIndicatorLook";
import UseWorkspaceMembershipAbilities from "@/hooks/useWorkspaceMembershipAbilities";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import SearchContainer from "../Search/SearchContainer";

const LEFT_PANEL_ID = "left-panel";
const RIGHT_PANEL_ID = "right-panel";

export default function NewDesignLayout({ children }: { children?: React.ReactNode }) {
  const size = useWindowSize();
  const parentRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = usePanelRef();
  const { settings, updateWebSettings } = useWebSettings();
  const columns = settings.layout.panelColumns;
  const setColumns = useCallback(
    (value: { left: number; right: number }) => {
      updateWebSettings({ layout: { panelColumns: value } });
    },
    [updateWebSettings],
  );
  const [collapseRightPanel, setCollapseRightPanel] = useState(false);
  const [panelSizes, setPanelSizes] = useState<{ left: number; right: number }>(columns);
  const [middleColInPixels, setMiddleColInPixels] = useState<number>(500);

  const location = useLocation();
  const bootBlocking = useBootIndicatorLook() === "blocking";
  const isDriverView = isDriverFeedPath(location.pathname);
  // Dispatch renders through the Outlet but, like the driver view, takes the full width beside the icon rail.
  const isDispatchView = isDispatchPath(location.pathname);
  const pathWorkspaceId = matchPath({ path: "/workspaces/:workspaceId", end: false }, location.pathname)?.params
    .workspaceId;
  const dispatchEnabled = useDispatchEnabled(pathWorkspaceId);

  UseWorkspaceMembershipAbilities();

  const getResidedWidth = (): number => {
    const sidebarWidth = 60;
    const totalWidth = parentRef?.current?.offsetWidth;
    return totalWidth - sidebarWidth;
  };

  const determineMiddleColWidth = (left?: number): number => {
    const leftValue = left ?? panelSizes.left;
    const totalWidth = parentRef?.current?.offsetWidth;
    const leftPanelWidth = (leftValue / 100) * totalWidth;
    const sidebarWidth = 60;
    const value = leftPanelWidth - sidebarWidth;
    setMiddleColInPixels(() => value);
    return value;
  };

  const setPanelSizeValues = (data: Record<string, number>) => {
    const left = data[LEFT_PANEL_ID];
    const right = data[RIGHT_PANEL_ID];
    if (left == null || right == null) return;

    setPanelSizes({ left, right });
    determineMiddleColWidth(left);
    if (right > 0) {
      setColumns({ left, right });
    }
  };

  const handleRightPanelResize = (panelSize: PanelSize) => {
    setCollapseRightPanel(panelSize.asPercentage === 0);
  };

  useEffect(() => {
    if (isDriverView) return;
    const value = collapseRightPanel ? getResidedWidth() : determineMiddleColWidth();
    setMiddleColInPixels(value);
  }, [size, collapseRightPanel, isDriverView]);

  const closeRightPanel = () => {
    const panel = rightPanelRef.current;
    if (!panel || panel.isCollapsed()) return;
    setColumns({ left: panelSizes.left, right: panelSizes.right });
    panel.collapse();
  };

  const openRightPanel = useCallback(() => {
    const panel = rightPanelRef.current;
    if (panel?.isCollapsed()) panel.expand();
  }, []);

  useEffect(() => {
    if (isDriverView) return;
    if (isChannelFeedPath(location.pathname)) {
      openRightPanel();
    }
  }, [location.pathname, isDriverView, openRightPanel]);

  const showLayoutPanels = !children && !isDriverView && !isDispatchView;

  // The driver list renders from the pathname alone, so this is the one place that can keep it off screen while
  // Dispatch is on. The gate is subscribed: a dispatcher on the list when the setting turns on moves without a reload.
  // The redirect takes the list's slot, so the rail stays mounted through it.
  const dispatchRedirect = dispatchEnabled ? dispatchPathForDriverView(location.pathname) : null;

  return (
    <Box
      ref={parentRef}
      className="new-design"
      id="newDesignFlag"
      aria-busy={bootBlocking}
      sx={{ bgcolor: "#000", height: "100vh", overflow: "hidden", display: "flex", position: "relative" }}
    >
      <BootBlockingScope>
        {showLayoutPanels ? (
          <FeedPanelContext.Provider value={{ closePanel: closeRightPanel, openPanel: openRightPanel }}>
            <Group
              defaultLayout={{ [LEFT_PANEL_ID]: columns.left, [RIGHT_PANEL_ID]: columns.right }}
              onLayoutChanged={setPanelSizeValues}
            >
              <Panel id={LEFT_PANEL_ID}>
                <Box sx={{ display: "flex", height: "100%" }}>
                  <Box sx={{ width: "60px" }}>
                    <LeftSidebar />
                  </Box>
                  <Box sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
                    <div style={{ width: middleColInPixels ? `${middleColInPixels}px` : "100%" }}>
                      <MiddleColumn />
                    </div>
                  </Box>
                </Box>
              </Panel>
              <Separator className="separator" style={collapseRightPanel ? { display: "none" } : undefined} />
              <Panel
                id={RIGHT_PANEL_ID}
                maxSize="50"
                minSize="20"
                collapsible
                collapsedSize={0}
                panelRef={rightPanelRef}
                onResize={handleRightPanelResize}
                style={{ background: "#000" }}
              >
                <Outlet />
              </Panel>
            </Group>
          </FeedPanelContext.Provider>
        ) : (
          <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
            <Box sx={{ width: "60px" }}>
              <LeftSidebar />
            </Box>
            <Box sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
              {dispatchRedirect ? (
                <Navigate to={dispatchRedirect} replace />
              ) : (
                (children ?? (isDispatchView ? <Outlet /> : <MiddleColumn />))
              )}
            </Box>
          </Box>
        )}
      </BootBlockingScope>
      <SearchContainer />
      <InviteToWorkspaceModal />
    </Box>
  );
}
